import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, toggleOpenFieldSection } from '../store';
import { RepoLegalIssues, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface RepoLegalIssuesProps {
    legalIssues: RepoLegalIssues;
    previousLegalIssues: RepoLegalIssues | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    nettingEngineFields?: string[];
}

export const RepoLegalIssuesSection: React.FC<RepoLegalIssuesProps> = ({ legalIssues, previousLegalIssues, isEditing, isUpdating, fieldsUpdated, nettingEngineFields }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'legalIssues';
    const { nettingLegislation, governingLawRecognised, submissionWillBeUpheld, judgmentOfCourtsEnforced, onShoreInsolvency, offShoreInsolvency, masterAgreementEnforceableObligations, capacityCounterpartyIssues, enforceabilityTransactionIssues } = legalIssues;

    const getPreviousField = useCallback((fieldId: keyof RepoLegalIssues) => !isNull(previousLegalIssues) && previousLegalIssues[fieldId] || null, [previousLegalIssues]);

    return (
        <SectionWrapper id={sectionId} label='Legal Issues' isEditing={isEditing} isUpdating={isUpdating} nettingEngineFields={nettingEngineFields}>
            <div className={styles.legalIssuesSectionWrapper}>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={nettingLegislation}
                    previousField={getPreviousField('nettingLegislation')}
                    fieldId='nettingLegislation'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={governingLawRecognised}
                    previousField={getPreviousField('governingLawRecognised')}
                    fieldId='governingLawRecognised'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={submissionWillBeUpheld}
                    previousField={getPreviousField('submissionWillBeUpheld')}
                    fieldId='submissionWillBeUpheld'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={judgmentOfCourtsEnforced}
                    previousField={getPreviousField('judgmentOfCourtsEnforced')}
                    fieldId='judgmentOfCourtsEnforced'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={masterAgreementEnforceableObligations}
                    previousField={getPreviousField('masterAgreementEnforceableObligations')}
                    fieldId='masterAgreementEnforceableObligations'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={onShoreInsolvency}
                    previousField={getPreviousField('onShoreInsolvency')}
                    fieldId='onShoreInsolvency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={offShoreInsolvency}
                    previousField={getPreviousField('offShoreInsolvency')}
                    fieldId='offShoreInsolvency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={capacityCounterpartyIssues}
                    previousField={getPreviousField('capacityCounterpartyIssues')}
                    fieldId='capacityCounterpartyIssues'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={enforceabilityTransactionIssues}
                    previousField={getPreviousField('enforceabilityTransactionIssues')}
                    fieldId='enforceabilityTransactionIssues'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
            </div>
        </SectionWrapper>
    );
};
